import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageHeader, useWorkspace } from '../../components/layout/WorkspaceShell';
import Btn from '../../components/ui/Btn';
import { Card, Sparkline, Spinner } from '../../components/ui/primitives';
import { Icon } from '../../components/ui/Icon';
import { supabase } from '../../lib/supabase';
import { useIsMobile } from '../../lib/useBreakpoint';

// 8-week buckets for the volume chart
const WEEKS = 8;

// Donut color palette
const DONUT_COLORS = ['#0a0f2e', '#3dc75b', '#8be89e', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#c4c9dc', '#eef0f7'];

export default function OverviewPanel() {
  const { team, counts } = useWorkspace();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [stats, setStats]     = useState(null);
  const [chart, setChart]     = useState(null);
  const [donut, setDonut]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!team?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Pull all uploads + templates in parallel
        const [uploadsRes, templatesRes] = await Promise.all([
          supabase
            .from('document_uploads')
            .select('id, status, template_id, display_name, created_at')
            .eq('team_id', team.id),
          supabase
            .from('document_templates')
            .select('id, label')
            .eq('team_id', team.id),
        ]);

        if (cancelled) return;
        const uploads = uploadsRes.data || [];
        const templates = templatesRes.data || [];
        const tplLookup = Object.fromEntries(templates.map(t => [t.id, t.label]));

        // Stats
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);

        const thisMonth = uploads.filter(u => new Date(u.created_at) >= monthStart);
        const lastMonth = uploads.filter(u => {
          const d = new Date(u.created_at);
          return d >= lastMonthStart && d < monthStart;
        });
        const pending = uploads.filter(u => u.status === 'in_review').length;
        const approved = uploads.filter(u => u.status === 'approved').length;
        const total = uploads.length;
        const approvalPct = total > 0 ? Math.round((approved / total) * 100) : 0;

        const monthDelta = lastMonth.length === 0
          ? thisMonth.length > 0 ? '+new' : '—'
          : `${thisMonth.length >= lastMonth.length ? '+' : ''}${Math.round(((thisMonth.length - lastMonth.length) / lastMonth.length) * 100)}%`;

        // Sparkline data — 8 weekly buckets ending now
        const buckets = new Array(WEEKS).fill(0);
        const msPerWeek = 7 * 24 * 60 * 60 * 1000;
        const oldest = now.getTime() - WEEKS * msPerWeek;
        uploads.forEach(u => {
          const t = new Date(u.created_at).getTime();
          if (t < oldest) return;
          const idx = Math.min(WEEKS - 1, Math.floor((t - oldest) / msPerWeek));
          buckets[idx]++;
        });

        // Volume chart — same 8 weeks, but split by approved vs pending/other
        const approvedBuckets = new Array(WEEKS).fill(0);
        const pendingBuckets  = new Array(WEEKS).fill(0);
        uploads.forEach(u => {
          const t = new Date(u.created_at).getTime();
          if (t < oldest) return;
          const idx = Math.min(WEEKS - 1, Math.floor((t - oldest) / msPerWeek));
          if (u.status === 'approved') approvedBuckets[idx]++;
          else                          pendingBuckets[idx]++;
        });

        // Donut: collection by template
        const byTemplate = {};
        uploads.forEach(u => {
          const key = u.template_id
            ? (tplLookup[u.template_id] || 'Other')
            : (u.display_name || 'Unclassified');
          byTemplate[key] = (byTemplate[key] || 0) + 1;
        });
        const sortedTpls = Object.entries(byTemplate)
          .sort((a, b) => b[1] - a[1])
          .map(([label, val], i) => ({ label, val, color: DONUT_COLORS[i % DONUT_COLORS.length] }));

        // Roll up smaller slices into "Other" if more than 6
        let donutSegs = sortedTpls;
        if (sortedTpls.length > 6) {
          const top = sortedTpls.slice(0, 5);
          const rest = sortedTpls.slice(5);
          const otherTotal = rest.reduce((s, x) => s + x.val, 0);
          donutSegs = [...top, { label: 'Other', val: otherTotal, color: DONUT_COLORS[5] }];
        }

        if (cancelled) return;

        setStats({
          docsThisMonth: thisMonth.length,
          docsThisMonthDelta: monthDelta,
          docsThisMonthSpark: buckets,
          pending,
          approvalPct,
          totalDocs: total,
        });
        setChart({
          weeks: approvedBuckets.map((a, i) => ({ a, b: pendingBuckets[i] })),
          maxVal: Math.max(1, ...approvedBuckets.map((a, i) => a + pendingBuckets[i])),
          totalApproved: approved,
        });
        setDonut({
          segments: donutSegs,
          total,
        });
      } catch (err) {
        console.error('Overview load failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [team?.id]);

  if (loading || !stats) {
    return (
      <>
        <PageHeader title="Overview" subtitle={`Here's what's moving in ${team?.name || 'your workspace'}.`} />
        <Card style={{ padding: 48, textAlign: 'center' }}>
          <Spinner size={20} color="var(--accent)" />
          <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-3)' }}>Loading overview…</div>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Overview"
        subtitle={`Here's what's moving in ${team?.name || 'your workspace'}.`}
        actions={
          <>
            <Btn
              variant="primary"
              size="md"
              icon={Icon.plus}
              onClick={() => navigate(`/workspace/${team.id}/documents`)}
            >
              View documents
            </Btn>
          </>
        }
      />

      {/* Stat cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile
          ? 'repeat(2, 1fr)'
          : 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: isMobile ? 10 : 16, marginBottom: 24,
      }}>
        <StatCard
          label="Docs this month"
          value={stats.docsThisMonth}
          delta={stats.docsThisMonthDelta}
          trend="up"
          icon={Icon.docs}
          data={stats.docsThisMonthSpark}
        />
        <StatCard
          label="Pending review"
          value={stats.pending}
          delta={stats.pending > 0 ? 'needs attention' : 'all clear'}
          trend={stats.pending > 0 ? 'alert' : 'up'}
          icon={Icon.clock}
          danger={stats.pending > 0}
        />
        <StatCard
          label="Approval rate"
          value={`${stats.approvalPct}%`}
          delta={`${counts.documents || 0} total`}
          trend="up"
          icon={Icon.check}
        />
        <StatCard
          label="Members"
          value={counts.members || 0}
          delta={`${counts.templates || 0} templates`}
          trend="up"
          icon={Icon.users}
        />
      </div>

      {/* Volume chart + template donut */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 2fr) minmax(0, 1fr)',
        gap: isMobile ? 12 : 16,
        marginBottom: 24,
      }}>
        <Card style={{ padding: isMobile ? 16 : 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>Document intake volume</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Last 8 weeks · approved vs pending</div>
            </div>
          </div>
          <VolumeChart weeks={chart.weeks} maxVal={chart.maxVal} />
        </Card>

        <Card style={{ padding: isMobile ? 16 : 20 }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: 'var(--text)' }}>Collection by template</div>
          <TemplateDonut segments={donut.segments} total={donut.total} />
        </Card>
      </div>

      {/* CTA */}
      <Card style={{
        padding: isMobile ? 16 : 20,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: isMobile ? 12 : 16,
        background: 'linear-gradient(135deg, var(--accent-bg) 0%, var(--surface) 100%)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16,
          flex: isMobile ? undefined : 1, minWidth: 0,
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'var(--accent)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Icon.sparkle size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
              {stats.pending > 0 ? `You have ${stats.pending} document${stats.pending === 1 ? '' : 's'} to review` : 'All caught up!'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
              {stats.pending > 0 ? 'Get them approved or rejected to keep things moving.' : 'No pending uploads. Nice work.'}
            </div>
          </div>
        </div>
        {stats.pending > 0 && (
          <Btn
            variant="primary"
            size="md"
            iconRight={Icon.arrowR}
            onClick={() => navigate(`/workspace/${team.id}/documents`)}
            style={isMobile ? { width: '100%', justifyContent: 'center' } : undefined}
          >
            Review now
          </Btn>
        )}
      </Card>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// StatCard
// ────────────────────────────────────────────────────────────────────────────
function StatCard({ label, value, delta, trend, icon: I, data, danger }) {
  const isMobile = useIsMobile();
  return (
    <Card style={{ padding: isMobile ? 12 : 16 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: isMobile ? 6 : 8,
      }}>
        <span style={{
          fontSize: isMobile ? 11.5 : 12,
          color: 'var(--text-3)', fontWeight: 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {label}
        </span>
        <div style={{
          width: isMobile ? 24 : 28, height: isMobile ? 24 : 28,
          borderRadius: isMobile ? 7 : 8,
          background: danger ? 'var(--warn-bg)' : 'var(--accent-bg)',
          color: danger ? 'var(--warn)' : 'var(--accent-600)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <I size={isMobile ? 12 : 13} />
        </div>
      </div>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 6,
        marginBottom: isMobile ? 4 : 6,
      }}>
        <span style={{
          fontSize: isMobile ? 22 : 28,
          fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--text)',
        }}>
          {value}
        </span>
        <span style={{
          fontSize: isMobile ? 11 : 12, fontWeight: 600,
          color: trend === 'alert' ? 'var(--warn)' : 'var(--accent-600)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          {trend === 'up' && '↑'}
          {trend === 'alert' && <Icon.alert size={isMobile ? 11 : 12} strokeWidth={2.5} />}
          {delta}
        </span>
      </div>
      {data && data.length > 0
        ? <Sparkline data={data} height={isMobile ? 18 : 24} width={isMobile ? 100 : 160} />
        : <div style={{ height: isMobile ? 0 : 24 }} />
      }
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// VolumeChart — stacked bars: approved (bottom) + pending (top)
// ────────────────────────────────────────────────────────────────────────────
function VolumeChart({ weeks, maxVal }) {
  const max = Math.max(maxVal, 5); // never go below scale of 5 to look right
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 180 }}>
        {weeks.map((w, i) => {
          const aH = (w.a / max) * 160;
          const bH = (w.b / max) * 160;
          return (
            <div key={i} style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 4,
            }}>
              <div style={{
                width: '100%', display: 'flex', flexDirection: 'column-reverse',
                alignItems: 'center', gap: 1, height: 160,
              }}>
                {w.a > 0 && (
                  <div style={{
                    width: '70%', height: aH,
                    background: 'var(--accent)', borderRadius: 3,
                  }} />
                )}
                {w.b > 0 && (
                  <div style={{
                    width: '70%', height: bH,
                    background: '#8be89e', borderRadius: 3, opacity: 0.55,
                  }} />
                )}
              </div>
              <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
                W{i + 1}
              </span>
            </div>
          );
        })}
      </div>
      <div style={{
        display: 'flex', gap: 16, marginTop: 12,
        paddingTop: 12, borderTop: '1px solid var(--border)',
        fontSize: 12, color: 'var(--text-2)',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, background: 'var(--accent)', borderRadius: 2 }} />
          Approved
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, background: '#8be89e', borderRadius: 2, opacity: 0.7 }} />
          Pending / other
        </span>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TemplateDonut — circular breakdown of uploads by template
// ────────────────────────────────────────────────────────────────────────────
function TemplateDonut({ segments, total }) {
  if (total === 0) {
    return (
      <div style={{
        height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 8, color: 'var(--text-3)',
      }}>
        <Icon.folder size={32} stroke="var(--text-muted)" />
        <div style={{ fontSize: 12 }}>No uploads yet</div>
      </div>
    );
  }

  const R = 56;
  const cx = 70, cy = 70;
  let offset = 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
        <svg width="140" height="140">
          {segments.map((s, i) => {
            const frac = s.val / total;
            const dash = 2 * Math.PI * R * frac;
            const gap = 2 * Math.PI * R - dash;
            const rot = (offset / total) * 360 - 90;
            offset += s.val;
            return (
              <circle
                key={i}
                cx={cx} cy={cy} r={R}
                stroke={s.color} strokeWidth="18" fill="none"
                strokeDasharray={`${dash} ${gap}`}
                transform={`rotate(${rot} ${cx} ${cy})`}
              />
            );
          })}
          <text x={cx} y={cy - 4} textAnchor="middle"
            style={{ fontSize: 22, fontWeight: 700, fill: 'var(--text)' }}>
            {total}
          </text>
          <text x={cx} y={cy + 14} textAnchor="middle"
            style={{ fontSize: 10, fill: 'var(--text-3)' }}>
            Total docs
          </text>
        </svg>
      </div>
      <div>
        {segments.map((s, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '4px 0', fontSize: 12,
          }}>
            <span style={{
              width: 8, height: 8, background: s.color, borderRadius: 2, flexShrink: 0,
            }} />
            <span style={{
              flex: 1, color: 'var(--text-2)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {s.label}
            </span>
            <span style={{
              fontWeight: 600, fontVariantNumeric: 'tabular-nums',
              color: 'var(--text)',
            }}>
              {s.val}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
